import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { useEffect, useRef } from 'react';
import { hasAnySchedulingAccess } from '@/features/scheduling/lib/permissions';
import type { AppRole } from '@/types/auth';

interface GuardProps {
  children: React.ReactNode;
}

/**
 * Strict guard: only platform admins ("admin" role) may pass.
 * Use for system-wide management surfaces.
 */
export function PlatformAdminRoute({ children }: GuardProps) {
  const { isAuthenticated, isLoading, hasRole } = useAuth();
  const location = useLocation();
  const hasShownToast = useRef(false);
  const isAdmin = hasRole('admin');

  useEffect(() => {
    if (!isLoading && isAuthenticated && !isAdmin && !hasShownToast.current) {
      hasShownToast.current = true;
      toast.error('Access denied. Admin privileges required.');
    }
  }, [isLoading, isAuthenticated, isAdmin]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/auth" state={{ from: location }} replace />;
  if (!isAdmin) {
    // Network admins should not bounce to /dashboard — send them to their allowed admin home.
    if (hasRole('network_admin')) return <Navigate to="/admin/podcasts" replace />;
    if (hasRole('studio_admin')) return <Navigate to="/admin/studio-bookings" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}

/**
 * Permissive guard: platform, network, studio OR scheduling admins may pass.
 * Use for the outer AdminLayout — individual routes still enforce role-specific access.
 */
export function AnyAdminRoute({ children }: GuardProps) {
  const { isAuthenticated, isLoading, hasRole } = useAuth();
  const location = useLocation();
  const hasShownToast = useRef(false);
  const isAnyAdmin = hasRole('admin') || hasRole('network_admin') || hasRole('studio_admin') || hasRole('scheduling_admin');

  useEffect(() => {
    if (!isLoading && isAuthenticated && !isAnyAdmin && !hasShownToast.current) {
      hasShownToast.current = true;
      toast.error('Access denied. Admin privileges required.');
    }
  }, [isLoading, isAuthenticated, isAnyAdmin]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/auth" state={{ from: location }} replace />;
  if (!isAnyAdmin) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

/**
 * Studio-admin-or-platform-admin guard. Used on /admin/studios and /admin/studio-bookings.
 * Rejects network_admin (they should not see studios surfaces).
 */
export function StudioAdminRoute({ children }: GuardProps) {
  const { isAuthenticated, isLoading, hasRole } = useAuth();
  const location = useLocation();
  const isAllowed = hasRole('admin') || hasRole('studio_admin');

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/auth" state={{ from: location }} replace />;
  if (!isAllowed) {
    if (hasRole('network_admin')) return <Navigate to="/admin/podcasts" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}

/**
 * Backwards-compatible alias — equivalent to PlatformAdminRoute.
 * Existing imports of `AdminProtectedRoute` keep working.
 */
export const AdminProtectedRoute = PlatformAdminRoute;

/**
 * Scheduling guard. Platform admins and scheduling admins may pass; everyone
 * else is sent back to an area they can actually use.
 */
export function SchedulingAdminRoute({ children }: GuardProps) {
  const { isAuthenticated, isLoading, hasRole } = useAuth();
  const location = useLocation();
  const isAllowed = hasRole('admin') || hasRole('scheduling_admin');

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/auth" state={{ from: location }} replace />;
  if (!isAllowed) {
    if (hasRole('network_admin')) return <Navigate to="/admin/podcasts" replace />;
    if (hasRole('studio_admin')) return <Navigate to="/admin/studio-bookings" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}

/**
 * Personal-scheduling guard for "My availability".
 *
 * Managing your own hours is a self-scoped permission
 * (`scheduling.personal_availability.manage`), so every admin-type account may
 * reach this page — not just scheduling admins. Podcasters and signed-out
 * visitors are still turned away.
 */
export function SchedulingSelfRoute({ children }: GuardProps) {
  const { isAuthenticated, isLoading, roles } = useAuth();
  const location = useLocation();
  const isAllowed = hasAnySchedulingAccess((roles ?? []) as AppRole[]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/auth" state={{ from: location }} replace />;
  if (!isAllowed) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}
